import type { SortOrder } from '@getaccept/fried-tofu';
import type { ParticipantRole } from '@getaccept/lib-shared/src/dealroom/types/enums/participant-role';
import type { NotificationChannel } from '../../../types/enums/notification-channel';
import type { AccessRule } from '../../../types/access-rule';
import type { DealroomFeatureSettings } from '../../../types/dsr';
import type { TaskStatus } from '../../../mutual-action-plan/types/enums/task-status';
import type { ActionPlanDto } from '../../mutual-action-plan/types/update-action-plan-dto';
import type { UpdateTaskDto } from '../../mutual-action-plan/types/update-task-dto';
import type { CommentStatus } from '../../../contextual-commenting/types/comment-status';

export enum DealroomEventType {
  Visited = 'visited',
  Published = 'published',
  Unpublished = 'unpublished',
  PostCreated = 'postCreated',
  ActionPlanUpdated = 'actionPlanUpdated',
  MilestoneCreated = 'milestoneCreated',
  MilestoneDeleted = 'milestoneDeleted',
  TaskCreated = 'taskCreated',
  TaskDeleted = 'taskDeleted',
  TaskUpdated = 'taskUpdated',
  FileUploaded = 'fileUploaded',
  FileDownloaded = 'fileDownloaded',
  FileDeleted = 'fileDeleted',
  Deleted = 'deleted',
  FeatureSettingsUpdated = 'featureSettingsUpdated',
  AccessRuleUpdated = 'accessRuleUpdated',
  ParticipantCreated = 'participantCreated',
  ParticipantDeleted = 'participantDeleted',
  ConversationMessageCreated = 'conversationMessageCreated',
  ContextualCommentReplyCreated = 'contextualCommentReplyCreated',
  ContextualCommentUpdated = 'contextualCommentUpdated',
  RoomOwnerUpdated = 'roomOwnerUpdated',
}

export enum EventTriggeredByType {
  Participant = 'participant',
  EntityAdmin = 'entityAdmin',
  System = 'system',
}

export enum LogSortBy {
  CreatedAt = 'createdAt',
}

export enum ParticipantCreationSource {
  SenderSite = 'senderSite',
  ParticipantSiteInvite = 'participantSiteInvite',
  UniversalLink = 'universalLink',
  API = 'API',
}

export interface ListEventLogsArgs {
  dealroomId: string;
  limit?: number;
  offset?: number;
  sortBy?: LogSortBy;
  sortOrder?: SortOrder;
  timeInterval?: string;
  participantIds?: string[];
  eventTypes?: DealroomEventType[];
}

export interface ListLogsResult {
  logs: Log[];
  totalCount: number;
}

export interface Log {
  id: string;
  data: EventLogData;
  createdAt: number;
}

export interface ContextualCommentUpdateData {
  status: CommentStatus;
}

export interface VisitedEventLogData {
  type: DealroomEventType.Visited;
  participantId: string;
  sessionId: string;
  ip: string;
  userAgent: string;
  trackingConsent: boolean;
  isFirstVisit: boolean;
  participantData?: ParticipantData;
}

export interface PublishedEventLogData {
  type: DealroomEventType.Published;
  version: number;
  participantId: string;
  participantData?: ParticipantData;
  __typename: string;
}

export interface UnpublishedEventLogData {
  type: DealroomEventType.Unpublished;
  participantId: string;
  participantData?: ParticipantData;
}

export interface PostCreatedEventLogData {
  type: DealroomEventType.PostCreated;
  participantId: string;
  postId: string;
  notificationChannels: NotificationChannel[];
  participantData?: ParticipantData;
}

export interface ActionPlanUpdatedEventLogData {
  type: DealroomEventType.ActionPlanUpdated;
  participantId: string;
  actionPlanId: string;
  updateActionPlan: ActionPlanDto;
  participantData?: ParticipantData;
}

export interface MilestoneEventLogData {
  type: DealroomEventType.MilestoneCreated | DealroomEventType.MilestoneDeleted;
  participantId: string;
  actionPlanId: string;
  milestoneId: string;
  participantData?: ParticipantData;
}

export interface TaskEventLogData {
  type: DealroomEventType.TaskCreated | DealroomEventType.TaskDeleted;
  participantId: string;
  actionPlanId: string;
  milestoneId: string;
  taskId: string;
  participantData?: ParticipantData;
}

export interface TaskData {
  title: string;
  description?: string;
  status: TaskStatus;
  participantIds: string[];
}

export interface TaskUpdatedEventLogData {
  type: DealroomEventType.TaskUpdated;
  participantId: string;
  actionPlanId: string;
  actionPlanTitle: string;
  milestoneId: string;
  milestoneIndex: number;
  milestoneTitle: string;
  taskId: string;
  taskData: TaskData;
  updateTask: UpdateTaskDto;
  participantData?: ParticipantData;
}

export interface FileData {
  name: string;
  extension: string;
}

export interface FileUploadedEventLogData {
  type: DealroomEventType.FileUploaded;
  fileId: string;
  size: number;
  participantId: string;
  fileData?: FileData;
  participantData?: ParticipantData;
}

export interface FileDownloadedEventLogData {
  type: DealroomEventType.FileDownloaded;
  fileId: string;
  participantId: string;
  fileData?: FileData;
  participantData?: ParticipantData;
}

export interface FileDeletedEventLogData {
  type: DealroomEventType.FileDeleted;
  fileId: string;
  participantId: string;
  fileData?: FileData;
  participantData?: ParticipantData;
}

export interface ContextualCommentReplyCreatedEventLogData {
  type: DealroomEventType.ContextualCommentReplyCreated;
  commentId: string;
  replyId: string;
  participantId: string;
  participantData?: ParticipantData;
}

export interface ContextualCommentUpdatedEventLogData {
  type: DealroomEventType.ContextualCommentUpdated;
  commentId: string;
  participantId: string;
  status: CommentStatus;
  updatedData: ContextualCommentUpdateData;
  participantData?: ParticipantData;
}

export interface DeletedEventLogData {
  type: DealroomEventType.Deleted;
  participantData?: ParticipantData;
}

export interface FeatureSettingsUpdatedEventLogData {
  type: DealroomEventType.FeatureSettingsUpdated;
  updateFeatureSettings: DealroomFeatureSettings;
  participantId: string;
  participantData?: ParticipantData;
}

export interface AccessRuleUpdatedEventLogData {
  type: DealroomEventType.AccessRuleUpdated;
  participantId: string;
  update: AccessRule;
  participantData?: ParticipantData;
}

export interface ParticipantCreatedEventLogData {
  type: DealroomEventType.ParticipantCreated;
  createdParticipantId: string;
  creationSource: ParticipantCreationSource;
  createdParticipantData: ParticipantData;
  participantData?: ParticipantData;
  participantId: string;
  userId?: string;
}

export interface ConversationMessageEventLogData {
  type: DealroomEventType.ConversationMessageCreated;
  participantId: string;
  conversationId: string;
  messageId: string;
  participantData?: ParticipantData;
}

export interface ParticipantData {
  role: ParticipantRole;
  firstName: string;
  lastName: string;
  title: string;
  companyName: string;
  email: string;
}

export interface ParticipantDeletedEventLogData {
  type: DealroomEventType.ParticipantDeleted;
  participantId: string;
  deletedParticipant: ParticipantData & { participantId: string };
  participantData?: ParticipantData;
}

interface RoomOwnerUpdatedEventLogData {
  type: DealroomEventType.RoomOwnerUpdated;
  oldRoomOwnerUserId: string;
  newRoomOwnerUserId: string;
}

export interface EntityAdminRoomOwnerUpdatedEventLogData extends RoomOwnerUpdatedEventLogData {
  userId: string;
}

export interface ParticipantRoomOwnerUpdatedEventLogData extends RoomOwnerUpdatedEventLogData {
  participantId: string;
}

export type EventLogData =
  | VisitedEventLogData
  | PublishedEventLogData
  | UnpublishedEventLogData
  | PostCreatedEventLogData
  | ActionPlanUpdatedEventLogData
  | TaskUpdatedEventLogData
  | FileUploadedEventLogData
  | FileDownloadedEventLogData
  | FileDeletedEventLogData
  | ContextualCommentReplyCreatedEventLogData
  | ContextualCommentUpdatedEventLogData
  | DeletedEventLogData
  | FeatureSettingsUpdatedEventLogData
  | AccessRuleUpdatedEventLogData
  | ParticipantCreatedEventLogData
  | ParticipantDeletedEventLogData
  | ConversationMessageEventLogData
  | ParticipantRoomOwnerUpdatedEventLogData
  | EntityAdminRoomOwnerUpdatedEventLogData;
